/**
 * WKT (Well-Known Text) Parser Utilities
 * Utilities for parsing WKT format geographic data
 */

/**
 * Parse a single ring of coordinates from WKT format
 * @param ringString - String containing coordinate pairs "lng lat, lng lat, ..."
 * @returns Array of [lat, lng] coordinate pairs for Leaflet
 */
function parseRing(ringString: string): Array<[number, number]> {
  const coordPairs = ringString
    .split(',')
    .map((pair) => pair.trim())
    .filter((pair) => pair);

  return coordPairs
    .map((pair) => {
      const parts = pair.trim().split(/\s+/);
      if (parts.length >= 2) {
        const lng = parseFloat(parts[0]);
        const lat = parseFloat(parts[1]);
        // Return as [lat, lng] for Leaflet
        if (!isNaN(lat) && !isNaN(lng)) {
          return [lat, lng] as [number, number];
        }
      }
      return null;
    })
    .filter((coord): coord is [number, number] => coord !== null);
}

/**
 * Extract all rings from a polygon string
 * Example input: "(10 10, 40 20, 20 40, 0 20), (10 20, 30 20, 20 30)"
 * @param polygonContent - The content inside POLYGON((...))
 * @returns Array of rings, each ring is an array of [lat, lng]
 */
function extractRingsFromPolygon(polygonContent: string): Array<Array<[number, number]>> {
  const rings: Array<Array<[number, number]>> = [];

  // Match each ring: (coordinates)
  const ringRegex = /\(([^()]+)\)/g;
  let match;

  while ((match = ringRegex.exec(polygonContent)) !== null) {
    const ringCoords = parseRing(match[1]);
    if (ringCoords.length > 0) {
      rings.push(ringCoords);
    }
  }

  return rings;
}

/**
 * Extract coordinates from WKT (Well-Known Text) format
 * Supports POLYGON and MULTIPOLYGON with holes
 *
 * @param wkt - WKT string to parse
 * @returns Array structure for Leaflet:
 *   - For POLYGON: [[[outer ring], [hole1], [hole2], ...]]
 *   - For MULTIPOLYGON: [
 *       [[polygon1 outer], [polygon1 hole1], ...],
 *       [[polygon2 outer], [polygon2 hole1], ...],
 *       ...
 *     ]
 *
 * @example
 * // POLYGON with hole:
 * // POLYGON ((10 10, 40 20, 20 40, 0 20), (10 20, 30 20, 20 30))
 * // Returns: [
 * //   [[10, 10], [20, 40], [40, 20], [20, 0]],  // outer ring (converted to [lat, lng])
 * //   [[20, 10], [30, 20], [20, 30]]            // hole
 * // ]
 *
 * @example
 * // MULTIPOLYGON:
 * // MULTIPOLYGON (((10 10, 40 20, 20 40)), ((50 50, 60 60, 70 50)))
 * // Returns: [
 * //   [[[10, 10], [20, 40], [40, 20]]],         // first polygon
 * //   [[[50, 50], [60, 60], [50, 70]]]          // second polygon
 * // ]
 */
export function extractCoordinatesFromWKT(wkt: string): Array<Array<Array<[number, number]>>> {
  try {
    const trimmedWkt = wkt.trim().toUpperCase();
    const originalWkt = wkt.trim(); // Keep original case for coordinate parsing

    // Handle MULTIPOLYGON
    if (trimmedWkt.startsWith('MULTIPOLYGON')) {
      return parseMultiPolygon(originalWkt);
    }

    // Handle POLYGON
    if (trimmedWkt.startsWith('POLYGON')) {
      const rings = parsePolygon(originalWkt);
      // Wrap in array to match multipolygon structure: [polygon]
      return rings.length > 0 ? [rings] : [];
    }

    // Unknown format, try to parse as simple coordinates
    return [];
  } catch (error) {
    console.warn('Error parsing WKT:', error);
    return [];
  }
}

/**
 * Parse a POLYGON WKT string
 * @param wkt - Full POLYGON WKT string
 * @returns Array of rings [outerRing, hole1, hole2, ...]
 */
function parsePolygon(wkt: string): Array<Array<[number, number]>> {
  // Remove "POLYGON" prefix (case-insensitive)
  let content = wkt.replace(/^POLYGON\s*/i, '').trim();

  // Remove the outermost parentheses if present
  if (content.startsWith('(') && content.endsWith(')')) {
    content = content.slice(1, -1).trim();
  }

  return extractRingsFromPolygon(content);
}

/**
 * Parse a MULTIPOLYGON WKT string
 * @param wkt - Full MULTIPOLYGON WKT string
 * @returns Array of polygons, each polygon is an array of rings
 */
function parseMultiPolygon(wkt: string): Array<Array<Array<[number, number]>>> {
  const polygons: Array<Array<Array<[number, number]>>> = [];

  // Remove "MULTIPOLYGON" prefix
  let content = wkt.replace(/^MULTIPOLYGON\s*/i, '').trim();

  // Remove the outermost parentheses
  if (content.startsWith('(') && content.endsWith(')')) {
    content = content.slice(1, -1).trim();
  }

  // Split polygons by ")),((" or similar patterns
  // Each polygon is enclosed in ((...), (...))
  const polygonStrings = splitMultiPolygonContent(content);

  for (const polygonStr of polygonStrings) {
    const rings = extractRingsFromPolygon(polygonStr);
    if (rings.length > 0) {
      polygons.push(rings);
    }
  }

  return polygons;
}

/**
 * Split MULTIPOLYGON content into individual polygon strings
 * Handles nested parentheses correctly
 * @param content - Content after removing "MULTIPOLYGON ("
 * @returns Array of polygon content strings
 */
function splitMultiPolygonContent(content: string): string[] {
  const polygons: string[] = [];
  let depth = 0;
  let currentPolygon = '';
  let inPolygon = false;

  for (let i = 0; i < content.length; i++) {
    const char = content[i];

    if (char === '(') {
      depth++;
      if (depth === 1) {
        // Start of a new polygon
        inPolygon = true;
        currentPolygon = '';
      } else {
        currentPolygon += char;
      }
    } else if (char === ')') {
      depth--;
      if (depth === 0 && inPolygon) {
        // End of current polygon
        polygons.push(currentPolygon.trim());
        inPolygon = false;
        currentPolygon = '';
      } else if (depth > 0) {
        currentPolygon += char;
      }
    } else if (inPolygon) {
      currentPolygon += char;
    }
  }

  // Handle case where last polygon wasn't closed properly
  if (currentPolygon.trim()) {
    polygons.push(currentPolygon.trim());
  }

  return polygons;
}

/**
 * Legacy function - Extract coordinates as flat array (for backward compatibility)
 * Returns only the outer ring of the first polygon
 * @param wkt - WKT string to parse
 * @returns Array of [lat, lng] coordinate pairs for Leaflet
 * @deprecated Use extractCoordinatesFromWKT for full polygon/multipolygon support
 */
export function extractCoordinatesFromWKTFlat(wkt: string): Array<[number, number]> {
  const result = extractCoordinatesFromWKT(wkt);
  // Return outer ring of first polygon, or empty array
  return result[0]?.[0] ?? [];
}

export function parseWktPoint(wkt: string | null | undefined): { lat: number; lng: number } | null {
  if (!wkt) return null;
  const match = /POINT\s*\(\s*([^\s]+)\s+([^\s]+)\s*\)/i.exec(wkt);
  if (match) {
    return {
      lng: parseFloat(match[1]),
      lat: parseFloat(match[2]),
    };
  }
  return null;
}
